import React, { useState, memo, useEffect } from "react";

// Memoized FAQ Item Component
const FaqItem = memo(({ faq, index, isOpen, onToggle }) => (
  <li key={index} className="faq-item">
    <button
      className="faq-question"
      onClick={() => onToggle(index)}
      aria-expanded={isOpen}
      aria-controls={`faq-answer-${index}`}
    >
      {faq.question}
    </button>
    {isOpen && (
      <p className="faq-answer" id={`faq-answer-${index}`}>
        {faq.answer}
      </p>
    )}
  </li>
));

function FAQ() {
  const [faqOpen, setFaqOpen] = useState(Array(16).fill(false));

  useEffect(() => {
    document.title = 'FAQ | Sapium Beauty - Laser Hair Removal & Skin Treatment Questions';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Frequently asked questions about laser hair removal and professional skin treatments at Sapium Beauty in East London (Canning Town). Learn about sessions, safety, preparation, aftercare and pricing.'
      );
    }
  }, []);

  const toggleFaq = (index) => {
    setFaqOpen(prev => {
      const newFaqOpen = [...prev];
      newFaqOpen[index] = !newFaqOpen[index]; // Toggle the specific index
      return newFaqOpen;
    });
  };

  const faqs = [
    {
      question: "How does laser hair removal work?",
      answer: "Laser hair removal works by targeting the pigment (melanin) in hair follicles with a concentrated beam of light. The pigment absorbs this light energy, effectively damaging the follicle and inhibiting future hair growth."
    },
    {
      question: "Is laser hair removal permanent?",
      answer: "While laser hair removal can significantly reduce hair growth, it may not be 100% permanent for everyone. Many clients experience long-term hair reduction, but some maintenance treatments might be necessary for completely smooth skin, especially if hormonal changes or other factors stimulate new growth."
    },
    {
      question: "What areas can be treated with laser hair removal?",
      answer: "Laser hair removal can be used on nearly any part of the body, including the face (upper lip, chin, sideburns), arms and underarms, legs, bikini area and Brazilian, back and chest. Our experts can tailor treatments to most body areas based on your preferences and needs."
    },
    {
      question: "Is laser hair removal safe?",
      answer: "Yes, laser hair removal is generally safe when performed by trained professionals using FDA-approved equipment. Our team takes care to assess your skin type, hair type, and other factors to minimize risks and ensure the best results."
    },
    {
      question: "How many sessions will I need?",
      answer: "The number of sessions varies depending on the treatment area, hair thickness, and individual response. Typically, clients require 6-8 sessions spaced about 4-6 weeks apart. During your consultation, our specialists will provide a personalized treatment plan."
    },
    {
      question: "Does laser hair removal hurt?",
      answer: "Most clients describe the sensation as similar to a rubber band snapping against the skin. While some discomfort is possible, many devices now include cooling mechanisms to make the treatment more comfortable. Topical numbing creams can also be applied for added comfort."
    },
    {
      question: "Is laser hair removal suitable for all skin types?",
      answer: "Modern laser technology has made it possible to safely treat a wide range of skin tones. However, some lasers are more effective on lighter skin with dark hair. During your consultation, we'll evaluate your skin and hair type to recommend the best options."
    },
    {
      question: "What should I do to prepare for a laser hair removal session?",
      answer: "To prepare for laser hair removal: Avoid sun exposure and tanning at least 2 weeks before treatment. Shave the area 24 hours before your appointment. Avoid waxing, plucking, or using depilatory creams for several weeks prior. Avoid applying makeup, lotions, or deodorant on the treatment area."
    },
    {
      question: "Are there any side effects?",
      answer: "Common side effects include redness, slight swelling, and a mild sunburn-like sensation, which usually subside within a few hours to a day. Rare side effects may include pigmentation changes, which are more likely to occur with darker skin tones or if post-care instructions aren't followed."
    },
    {
      question: "What should I do after my laser hair removal session?",
      answer: "Aftercare recommendations include: Avoid direct sun exposure and wear sunscreen. Avoid hot baths, saunas, or intense exercise for 24-48 hours. Moisturize the treated area with a gentle, fragrance-free lotion. Avoid picking or scratching the treated area."
    },
    {
      question: "Can I shave between laser hair removal sessions?",
      answer: "Yes, you can shave between sessions, but avoid plucking, waxing, or using hair removal creams, as these methods remove the hair follicle and can interfere with laser effectiveness."
    },
    {
      question: "How much does laser hair removal cost?",
      answer: "The cost of laser hair removal varies based on the size of the area being treated and the number of sessions needed. We offer personalized consultations to provide accurate pricing for your specific treatment plan."
    },
    {
      question: "Can I get laser hair removal while pregnant?",
      answer: "Laser hair removal is generally not recommended for pregnant women due to a lack of research on its effects during pregnancy. We advise waiting until after childbirth and consulting your healthcare provider."
    },
    {
      question: "How long does each session take?",
      answer: "Session times vary by area: Small areas (upper lip, chin): 5-10 minutes, Medium areas (underarms, bikini line): 15-30 minutes, Large areas (legs, back): 45-60 minutes."
    },
    {
      question: "When will I see the results?",
      answer: "Many clients notice a reduction in hair growth after the first few sessions. Full results are typically achieved after completing the recommended series of treatments. Hair will appear finer and thinner as you progress through your sessions."
    },
    {
      question: "Can I combine laser hair removal with other treatments?",
      answer: "Yes, laser hair removal can often be combined with other skincare treatments, but it's best to consult with our specialists to create a safe, customized plan based on your goals."
    }
  ];

  return (
    <div className="faq-section">
      <h2>Frequently Asked Questions</h2>
      <ul className="faq-list">
        {faqs.map((faq, index) => (
          <FaqItem
            key={index}
            faq={faq}
            index={index}
            isOpen={faqOpen[index]}
            onToggle={toggleFaq}
          />
        ))}
      </ul>
    </div>
  );
}

export default FAQ;